import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../lib/utils'

// The app's topbar chrome chip: a small, fully-rounded icon button that sits in a
// chrome bar (e.g. Cooking Mode's Exit / Dark / Keep-awake controls). Distinct from
// `Button` (form actions) and `PillButton` (compact CTAs) — it's lighter (12px label),
// icon-first, and collapses to icon-only on phones where the bar gets tight.
//
// `active` switches to the green "on" treatment for toggle uses (e.g. keep-awake). It's
// kept separate from `aria-pressed` on purpose: a toggle can want to read as "on" to a
// screen reader (the stored preference) while the live visual is off, or vice versa —
// pass `aria-pressed` through props independently.
const topbarButtonVariants = cva(
  'inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors max-sm:gap-0 max-sm:p-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      active: {
        false: 'border-border bg-card text-muted-foreground hover:border-brand-green-border hover:text-foreground',
        // Green "on" state — light + dark. Mirrors the keep-awake live indicator.
        true: 'border-brand-green-border bg-brand-green-tint text-brand-green-on-tint'
      }
    },
    defaultVariants: { active: false }
  }
)

export interface TopbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof topbarButtonVariants> {
  /** Leading icon — always visible (this is the only content shown on phones). */
  icon: React.ReactNode
}

// Label collapses to icon-only below `sm`; pass an `aria-label` so the control stays
// named when the text is hidden.
const TopbarButton = React.forwardRef<HTMLButtonElement, TopbarButtonProps>(
  ({ className, active, icon, children, type, ...props }, ref) => (
    <button ref={ref} type={type ?? 'button'} className={cn(topbarButtonVariants({ active, className }))} {...props}>
      {icon}
      {children != null && <span className="max-sm:hidden">{children}</span>}
    </button>
  )
)
TopbarButton.displayName = 'TopbarButton'

export { TopbarButton, topbarButtonVariants }
